// biome-ignore-all lint/a11y/useSemanticElements: <div> used because children may contain interactive elements
import { Eye, EyeOff, Lock } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { useRoles } from "@/features/admin/use-roles";
import type { Role } from "@/lib/auth-types";
import { getUnimplementedElementIds } from "@/lib/sidebar-config";
import { cn } from "@/lib/utils";
import { type ElementId, PINNED_ELEMENTS } from "@/lib/visibility-types";
import { useVisibility } from "./use-visibility";
import { useRoleResolve, useUpsertVisibilityRule } from "./use-visibility-query";
import { VisibilitySourceCard } from "./visibility-source-card";

interface VisibleProps {
	id: string;
	label?: string;
	children: React.ReactNode;
}

export function Visible({ id, label, children }: VisibleProps) {
	const {
		isVisible,
		isEditorMode,
		toggleVisibility,
		toggleVisibilityForAll,
		registerElement,
		selectedRole,
		setSelectedRole,
		getVisibleElements,
	} = useVisibility();
	const [isHovered, setIsHovered] = useState(false);
	const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

	// ALL hooks MUST be called before the early return to preserve hook order across
	// isEditorMode toggles. React crashes if hook count differs between renders.
	const { data: resolveData } = useRoleResolve(isEditorMode ? selectedRole : null);
	// Gate the /admin/roles fetch — non-admin users see 403 (Sentry FARMASTER-FRONTEND-1Y).
	// Editor mode is super_admin-only, so the role list is only needed there.
	const { getRoleLabel } = useRoles({ enabled: isEditorMode });
	const upsertMutation = useUpsertVisibilityRule();

	const resolved = useMemo(() => {
		const found = resolveData?.elements.find((el) => el.elementId === id);
		if (found) return found;
		return {
			elementId: id,
			effective: false,
			source: "none" as const,
			grantedByRole: "" as const,
			grantedByDisplay: null,
			depth: 0 as const,
		};
	}, [resolveData, id]);

	const cancelLongPress = () => {
		if (longPressTimer.current !== null) {
			clearTimeout(longPressTimer.current);
			longPressTimer.current = null;
		}
	};

	const handleTouchStart = () => {
		if (!isEditorMode || isPinned) return;
		longPressTimer.current = setTimeout(() => {
			longPressTimer.current = null;
			handleToggle();
		}, 500);
	};

	useEffect(() => {
		registerElement(id);
	}, [id, registerElement]);

	const visible = isVisible(id);

	if (!isEditorMode) {
		return visible ? children : null;
	}

	const isPinned = PINNED_ELEMENTS.has(id as ElementId);
	// Page not built yet — block granting it (would route to a 404). Disabling an
	// already-granted one stays allowed: only block when the toggle would enable.
	// `visible` reflects the selected role/group grant, so it works for both scopes
	// (parent-hidden-but-granted is a rare edge where this blocks a disable — fine).
	const isLocked = getUnimplementedElementIds().has(id);
	const blockEnable = isLocked && !visible;

	const handleToggle = () => {
		if (isPinned) {
			console.warn("Ten element nie może być ukryty dla super_admin");
			return;
		}
		if (blockEnable) {
			console.warn("Strona w przygotowaniu — nie można włączyć tego elementu");
			return;
		}

		toggleVisibility(id);
	};

	// onPointerDownCapture in capture phase intercepts before children handle it.
	// We also need onClickCapture because preventDefault on pointerdown does NOT
	// prevent the click event — without it, Ctrl+click on <Link> opens a new tab.
	const handlePointerDownCapture = (e: React.PointerEvent) => {
		if (!e.ctrlKey) return;
		e.preventDefault();
		e.stopPropagation();

		if (e.altKey) {
			if (isPinned) {
				console.warn("Ten element nie może być ukryty dla żadnej roli");
				return;
			}
			if (isLocked) {
				console.warn("Strona w przygotowaniu — nie można włączyć tego elementu");
				return;
			}
			toggleVisibilityForAll(id);
			return;
		}

		handleToggle();
	};

	const handleClickCapture = (e: React.MouseEvent) => {
		if (!e.ctrlKey) return;
		e.preventDefault();
		e.stopPropagation();
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (!e.ctrlKey || (e.key !== "Enter" && e.key !== " ")) return;
		e.preventDefault();
		e.stopPropagation();
		handleToggle();
	};

	const isNewElement =
		selectedRole !== null && !getVisibleElements(selectedRole).has(id) && !visible;

	const handleSwitchRole = (role: string) => setSelectedRole(role as Role);
	const handleEnableForCurrent = (elementId: string) => {
		if (!selectedRole || isLocked) return;
		upsertMutation.mutate({ role: selectedRole, elementId, visible: true });
	};

	const innerDiv = (
		<div
			className={cn(
				"relative cursor-default",
				!visible && "opacity-60",
				isHovered && visible && !isPinned && "ring-1 ring-primary/40 ring-offset-1",
				isHovered && !visible && "ring-1 ring-destructive/40 ring-offset-1",
			)}
			onPointerDownCapture={handlePointerDownCapture}
			onClickCapture={handleClickCapture}
			onKeyDown={handleKeyDown}
			role="button"
			tabIndex={0}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
			onTouchStart={handleTouchStart}
			onTouchEnd={cancelLongPress}
			onTouchMove={cancelLongPress}
			data-visibility-id={id}
		>
			{children}

			{!visible &&
				(isPinned ? (
					<div
						className="pointer-events-none absolute inset-0 flex items-center justify-center border-2 border-dashed border-muted-foreground/30 bg-muted/30"
						data-testid="visibility-hidden-indicator"
					>
						<EyeOff className="size-4 text-muted-foreground" />
					</div>
				) : (
					<div
						className="pointer-events-none absolute inset-0 flex items-center justify-center border-2 border-dashed border-muted-foreground/30 bg-muted/30"
						data-testid="visibility-hidden-indicator"
					>
						<EyeOff className="size-4 text-muted-foreground" />
					</div>
				))}

			{isHovered && (
				<div className="pointer-events-none absolute -top-6 left-0 z-[100] flex items-center gap-1 rounded bg-popover px-1.5 py-0.5 text-[10px] text-popover-foreground shadow-md border border-border whitespace-nowrap">
					{isPinned ? (
						<Lock className="size-3 text-status-warning-text" />
					) : visible ? (
						<Eye className="size-3 text-primary" />
					) : (
						<EyeOff className="size-3 text-muted-foreground" />
					)}
					<span className="font-mono">{id}</span>
					{label && <span className="text-muted-foreground">({label})</span>}
					{isLocked && <span className="text-status-warning-text">• w budowie</span>}
				</div>
			)}

			{isPinned && (
				<div className="pointer-events-none absolute -right-1 -top-1 z-[101]">
					<Lock className="size-3 text-status-warning-text" />
				</div>
			)}

			{isNewElement && (
				<div className="pointer-events-none absolute -right-1 -top-1 z-[101] rounded bg-status-info-bg px-1 py-0.5 text-[9px] font-bold text-status-info-text">
					NEW
				</div>
			)}
		</div>
	);

	return selectedRole ? (
		<HoverCard openDelay={300}>
			<HoverCardTrigger asChild>{innerDiv}</HoverCardTrigger>
			<HoverCardContent side="right" align="start" className="w-72">
				<VisibilitySourceCard
					resolved={resolved}
					currentRole={selectedRole}
					isPinned={isPinned}
					isLocked={isLocked}
					onSwitchRole={handleSwitchRole}
					onEnableForCurrent={handleEnableForCurrent}
					getRoleLabel={getRoleLabel}
				/>
			</HoverCardContent>
		</HoverCard>
	) : (
		innerDiv
	);
}
